
import { MetaMetrics, PeriodData } from '@/types/metaMetrics';

/**
 * Creates initial metrics data structure with zeroed values
 */
export function createInitialMetrics(): MetaMetrics {
  const emptyPeriodData: PeriodData = {
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    total: 0,
    byDay: [0, 0, 0, 0, 0, 0, 0]
  };
  
  return {
    leads: { ...emptyPeriodData },
    costs: { ...emptyPeriodData },
    clicks: { ...emptyPeriodData },
    impressions: { ...emptyPeriodData },
    campaignDetails: null,
    isLoading: false,
    error: null
  };
}

/**
 * Distributes a total value across days with some randomization
 * for more realistic charting
 */
export function distributeValue(total: number, days: number = 7): number[] {
  if (total <= 0) {
    return Array(days).fill(0);
  }
  
  const result = [];
  let remaining = total;
  
  // Distribute values across days with some randomness
  for (let i = 0; i < days - 1; i++) {
    // Get a random portion of the remaining value
    const portion = Math.round((Math.random() * 0.4 + 0.1) * (remaining / (days - i)));
    result.push(portion);
    remaining -= portion;
  }
  
  // Add remaining value to the last day
  result.push(Math.max(0, remaining));
  
  return result;
}

/**
 * Calculates growth percentage between two values
 */
export function calculateGrowth(current: number, previous: number): number {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  
  return Math.round(((current - previous) / previous) * 100);
}

/**
 * Returns a trend type based on value comparison
 * Changed 'neutral' to 'stable' to match the type definition
 */
export function getTrendType(value: number): 'up' | 'down' | 'stable' {
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'stable';
}

/**
 * Formats a number as currency string
 */
export function formatCurrency(value: number): string {
  return value.toLocaleString('de-DE', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }) + ' €';
}

/**
 * Formats a number as percentage string
 */
export function formatPercentage(value: number): string {
  return value.toLocaleString('de-DE', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }) + '%';
}
